import React from "react"

export enum OS {
  IOS = "IOS",
  IOSChrome = "IOSChrome",
  Android = "Android",
  AndroidTab = "AndroidTab",
  Desktop = "Desktop",
  ChromeDesktop = "ChromeDesktop",
  SamsungBrowser = "SamsungBrowser",
  IOSIpadChrome = "IOSIpadChrome"
}

interface IAgentRegMap {
  [propName: string]: RegExp
}

const AgentRegMap: IAgentRegMap = {
  IOSChrome: /CriOS/i,
  IOS: /iPad|iPhone|iPod/,
  ChromeDesktop: /Chrome/i,
  Android: /android/i,
  MobileAndroid: /Mobile/i,
  SamsungBrowser: /SamsungBrowser/i,
  IPad: /iPad/i,
  Safari: /^((?!chrome|android).)*safari/i,
  Mac: /Macintosh/i
}

export const useIsAndroidApp = (): boolean => {
  return !!window.AndroidNativeHandler?.eventOpenPageInSecureBrowser
}

export const useIsIOSApp = (): boolean => {
  return !!window.webkit?.messageHandlers?.eventOpenPageInSecureBrowser
}

export const useIsMobileApp = (): boolean => {
  return useIsAndroidApp() || useIsIOSApp()
}

export const isIpad = (): boolean =>
  navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform)

export const detectDeviceType = (): keyof IAgentRegMap => {
  const userAgent: string = navigator.userAgent || navigator.vendor
  if (AgentRegMap.IOSChrome.test(userAgent) && AgentRegMap.IPad.test(userAgent))
    return OS.IOSIpadChrome
  if (AgentRegMap.IOSChrome.test(userAgent)) return OS.IOSChrome
  if (AgentRegMap.SamsungBrowser.test(userAgent)) return OS.SamsungBrowser
  if (
    AgentRegMap.Android.test(userAgent) &&
    AgentRegMap.MobileAndroid.test(userAgent)
  )
    return OS.Android
  if (AgentRegMap.Android.test(userAgent)) return OS.AndroidTab
  if (AgentRegMap.ChromeDesktop.test(userAgent)) return OS.ChromeDesktop
  if ((AgentRegMap.IOS.test(userAgent) || isIpad()) && !window.MSStream)
    return OS.IOS
  return OS.Desktop
}

export const detectSafariDesktop = () => {
  const userAgent: string = navigator.userAgent || navigator.vendor
  return AgentRegMap.Safari.test(userAgent) && AgentRegMap.Mac.test(userAgent)
}

// Function to extract iOS version from the user agent string
const getIOSVersion = () => {
  const userAgent = navigator.userAgent || navigator.vendor;
  // Regular expression to match the iOS version in the user agent string
  const match = /(iPhone|iPad) OS ([0-9_]*)/g.exec(userAgent);
  // Extract the version string and replace underscores with dots
  const versionString = match ? match[2].replace('_', '.') : '0';
  return parseFloat(versionString);
};

// Function to check if the device is an iOS device based on the user agent
export const isIosDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor;
  return AgentRegMap.IOS.test(userAgent);
};

// Function to check if the iOS version is greater than 17
export const isIOSVersionGreaterThan17 = () => {
  try {
    // Check if the device is an iOS device and if the iOS version is greater than 17
    return isIosDevice() && getIOSVersion() >= 17;
  } catch (error) {
    return false;
  }
};
